import type { ReactNode } from 'react';
import { Tooltip } from 'antd';
import { ArrowUpOutlined, AudioOutlined, PlusOutlined } from '@ant-design/icons';

import { Badge, Button, Popover, Select, Spinner, Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { useSucKhoeTroLy } from '../../hooks/useChung';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import type { BacWeb } from '../../nghiep-vu/hoi';
import { khoaMacDinh, khoaModel, tenCongKhai, tenCongKhaiMacDinh } from '../../nghiep-vu/ten-model';
import { NutTraWeb, TuyChonTraLoi } from './TuyChonTraLoi';

export interface ThanhSoanProps {
  question: string;
  onQuestion: (v: string) => void;
  onGui: () => void;
  onDung: () => void;
  dangChay: boolean;
  placeholder: string;
  /** `false` thì nút gửi KHOÁ và nói vì sao (`lyDoKhoa`) — nút xám không phản ứng là chỗ người dùng kết luận sản phẩm hỏng. */
  guiDuoc: boolean;
  lyDoKhoa?: string;
  tuyChon: {
    mucVan: string;
    doDai: string;
    bieuDo: string;
    onMucVan: (v: string) => void;
    onDoDai: (v: string) => void;
    onBieuDo: (v: string) => void;
  };
  /** Có thì hiện công tắc tra web ba bậc trong khối nhập. */
  web?: { gia: string; onWeb: (v: BacWeb) => void };
  model: string;
  onModel: (v: string) => void;
  /**
   * Hỏi đáp AI hiện sẵn mục mặc định như một lựa chọn thật (chọn lại đúng mục ấy thì trả về rỗng — theo mặc định máy
   * chủ); chat với bot để trống và hiện tên mặc định làm chữ mờ, như native.
   */
  hienMacDinh?: boolean;
  /** Mic — chỉ dựng khi trình duyệt có nhận dạng giọng nói. */
  mic?: { dangNghe: boolean; batDau: () => void };
  /** Dòng cảnh báo phía trên khối nhập (mô hình chưa cấu hình, bot đang tắt…). */
  canhBao?: ReactNode;
  goiY: string;
}

/**
 * Ô soạn ghim đáy của hai màn hỏi–đáp: người ta đọc câu trả lời rồi hỏi tiếp, ô soạn ở đầu trang nghĩa là mỗi lần hỏi
 * tiếp phải cuộn ngược qua chính câu trả lời vừa đọc. Hai màn có cùng bộ công tắc nên dùng chung một khối — bày ra hai
 * kiểu là bắt người dùng học hai lần.
 */
export function ThanhSoan({
  question, onQuestion, onGui, onDung, dangChay, placeholder, guiDuoc, lyDoKhoa, tuyChon, web, model, onModel,
  hienMacDinh, mic, canhBao, goiY,
}: ThanhSoanProps) {
  const { t } = useT();
  const moTaLoi = useMoTaLoi();
  const health = useSucKhoeTroLy();
  const models = health.data?.models;
  const macDinh = khoaMacDinh(health.data?.model, models);

  return (
    <div className="shrink-0 px-4 pb-4 pt-2 lg:px-6">
      <div className="mx-auto w-full max-w-5xl">
        {canhBao}
        {/* Viền khối ngoài GIỮ LẠI: ô soạn là điều khiển để thao tác, ranh giới cần thấy được TRƯỚC khi bấm vào. */}
        <div className="rounded-lg border border-default bg-surface px-4 py-3 shadow-sm focus-within:border-primary-500">
          <Textarea
            autoSize={{ minRows: 1, maxRows: 6 }}
            aria-label={t('troLyAiHoi.cauHoi')}
            placeholder={placeholder}
            value={question}
            onChange={(e) => onQuestion(e.target.value)}
            // Ô nhập không viền riêng: đã nằm trong một khối có viền — vẽ thêm là hộp lồng trong hộp.
            variant="borderless"
            className="!bg-transparent !px-0 !shadow-none text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onGui();
              }
            }}
          />
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {/* Các công tắc TRÌNH BÀY gom vào nút `+`. ⚠️ Đánh đổi đã biết: gom vào menu là giấu trạng thái được nhớ qua
                các lần tải trang. */}
            <Popover
              trigger="click"
              placement="topLeft"
              content={
                <div className="w-[290px]">
                  <TuyChonTraLoi {...tuyChon} />
                </div>
              }
            >
              <Button
                size="sm"
                variant="secondary"
                disabled={dangChay}
                aria-label={t('troLyAiHoi.tuyChonTraLoi')}
                className="shrink-0"
                icon={<PlusOutlined />}
              />
            </Popover>

            {web ? <NutTraWeb web={web.gia} onWeb={web.onWeb} disabled={dangChay} /> : null}

            {/* Ô chọn mô hình: so hai mô hình trên cùng câu hỏi là việc thật sự cần. Mô hình CHƯA cấu hình vẫn hiện,
                chỉ bị khoá — giấu đi thì "không có" và "thiếu khoá" trông giống hệt nhau. Không hiện `m.model`/`m.note`:
                cả hai mang tên nhà cung cấp. */}
            {health.isPending ? (
              <Spinner size="small" />
            ) : health.isError ? (
              <Tooltip title={moTaLoi(health.error)}>
                <span>
                  <Badge variant="danger">{t('troLyAiHoi.khongTaiDuocMoHinh')}</Badge>
                </span>
              </Tooltip>
            ) : models?.length ? (
              <Select<string>
                aria-label={t('troLyAiHoi.moHinh')}
                size="small"
                className="w-40 shrink-0"
                value={(hienMacDinh ? model || macDinh : model) || undefined}
                placeholder={tenCongKhaiMacDinh(health.data?.model, models)}
                onChange={(v) => {
                  const k = v ?? '';
                  onModel(hienMacDinh && k === macDinh ? '' : k);
                }}
                options={models.map((m) => ({
                  value: khoaModel(m),
                  label: tenCongKhai(m),
                  disabled: !m.available,
                  title: m.available ? undefined : t('troLyAiHoi.chuaCauHinh'),
                }))}
              />
            ) : health.data?.model ? (
              <Badge variant={health.data.llmConfigured ? 'neutral' : 'warning'}>
                {tenCongKhaiMacDinh(health.data.model, models)}
              </Badge>
            ) : null}

            <span className="flex-1" />

            {dangChay ? (
              <Button
                size="sm"
                variant="destructive"
                // Ô vuông ĐẶC — quy ước của mọi khung chat cho DỪNG. antd không có icon này nên vẽ bằng CSS.
                icon={<span aria-hidden="true" className="block size-2.5 rounded-xs bg-current" />}
                onClick={onDung}
              >
                {t('troLyAiHoi.dung')}
              </Button>
            ) : (
              <>
                {mic ? (
                  <Tooltip title={mic.dangNghe ? t('troLyAiHoi.dangNghe') : t('troLyAiHoi.hoiBangGiongNoi')}>
                    <Button
                      size="sm"
                      shape="circle"
                      variant={mic.dangNghe ? 'destructive' : 'ghost'}
                      aria-label={mic.dangNghe ? t('troLyAiHoi.dungNghe') : t('troLyAiHoi.hoiBangGiongNoi')}
                      aria-pressed={mic.dangNghe}
                      icon={<AudioOutlined />}
                      onClick={mic.batDau}
                    />
                  </Tooltip>
                ) : null}
                {/* Nút gửi CHỈ HIỆN khi đã có chữ (`trim()` — gõ mỗi dấu cách mà nút hiện ra là bấm vào không có gì).
                    Có chữ mà chưa gửi được thì nút hiện KHOÁ kèm lý do. */}
                {question.trim() ? (
                  <Tooltip title={!guiDuoc ? lyDoKhoa : undefined}>
                    <span>
                      <Button
                        size="sm"
                        shape="circle"
                        variant="primary"
                        aria-label={t('troLyAiHoi.guiCauHoi')}
                        disabled={!guiDuoc}
                        icon={<ArrowUpOutlined />}
                        onClick={onGui}
                      />
                    </span>
                  </Tooltip>
                ) : null}
              </>
            )}
          </div>
        </div>
        <p className="mt-1.5 text-xs text-content-muted">{goiY}</p>
      </div>
    </div>
  );
}
